/**
 * Build a compact object for the encrypted `userDetails` cookie after `/auth/getUserDetail`.
 * The API returns a large `userObj` (populated partnerAdmin, institutions, milestones, etc.).
 * Spreading it into the cookie often exceeds ~4KB and corrupts or drops the payload so the
 * JWT (`token`) is lost — the next request then 401/403s and the app shows "session expired".
 *
 * @param {Record<string, unknown> | null | undefined} auth — Existing cookie payload after decrypt
 * @param {Record<string, unknown> | null | undefined} userObj — `getUserDetail` user object
 */
export function buildUserDetailsCookiePayload(auth, userObj) {
  if (!userObj) {
    return auth || {};
  }
  const slim = { ...userObj };
  delete slim.password;
  delete slim.partnerAdmin;
  delete slim.region;
  delete slim.institutions;
  delete slim.milestones;
  delete slim.customFields;
  const payload = {
    ...(auth || {}),
    fullName: `${userObj.preferredFname || ''} ${userObj.lname || ''}`.trim(),
    ...slim,
    // getUserDetail only nests partnerType under partnerAdmin, which is stripped above.
    partnerType: userObj.partnerAdmin?.partnerType ?? auth?.partnerType,
    sessionTimeoutNeeded: auth?.sessionTimeoutNeeded ?? userObj?.sessionTimeoutNeeded,
    token: auth?.token,
  };
  // Free/reduced lunch status is protected student data, and the cookie is readable on the client.
  // Dropped from the existing cookie too, in case an older payload already carried it.
  delete payload.frl;
  return payload;
}
